import React from 'react';

/**
 * Component Registry - Auto-discovery using webpack require.context
 * Exports a flat "global.Components" object.
 *
 * Components are discovered by scanning for PascalCase .tsx/.jsx files.
 * Pattern: ./components/folder-name/ComponentName.tsx → { ComponentName: Component }
 */

// Re-export types for backward compatibility
export type { AdditionalComponentProps, ComponentData } from './component-registry.types';

// Auto-discover all components in the components directory
// Matches: ./components/any-folder/PascalCaseFile.tsx or .jsx
const componentContext = require.context('./components', true, /\/([A-Z][a-zA-Z0-9]+)\.(tsx|jsx)$/);

export interface ComponentRegistry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [componentName: string]: React.ComponentType<any>;
}

/**
 * Build the component registry by scanning for PascalCase component files
 */
function buildComponentRegistry(): ComponentRegistry {
  const registry: ComponentRegistry = {};
  const duplicates: string[] = [];
  const failed: string[] = [];

  componentContext.keys().forEach((modulePath: string) => {
    const match = modulePath.match(/\/([A-Z][a-zA-Z0-9]+)\.(tsx|jsx)$/);
    if (!match) return;

    const componentName = match[1];
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    const module: { default?: React.ComponentType<any>; [key: string]: unknown } = componentContext(modulePath);

    // Support default export, named export matching filename, or single export
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const component = module.default ?? (module[componentName] as React.ComponentType<any> | undefined);

    if (!component) {
      failed.push(`${componentName} (${modulePath})`);
      return;
    }

    if (registry[componentName]) {
      duplicates.push(componentName);
    }

    registry[componentName] = component;
  });

  // Log warnings only for development
  if (process.env.NODE_ENV !== 'production') {
    if (failed.length > 0) {
      // eslint-disable-next-line no-console
      console.warn('[Component Registry] Failed to load components:', failed.join(', '));
    }

    if (duplicates.length > 0) {
      // eslint-disable-next-line no-console
      console.warn('[Component Registry] Duplicate component names found:', duplicates.join(', '));
    }

    // eslint-disable-next-line no-console
    console.log(`[Component Registry] Loaded ${Object.keys(registry).length} components`);
  }

  return registry;
}

/**
 * The component registry as a flat object.
 *
 * Usage (server-side): global.Components = Components
 * Usage (client-side): window.Components = Components
 */
export const Components = buildComponentRegistry();

/**
 * Safe component lookup helper
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getComponent(name: string): React.ComponentType<any> | undefined {
  return Components[name];
}

/**
 * Get all registered component names (sorted alphabetically)
 */
export function getRegisteredComponentNames(): string[] {
  return Object.keys(Components).sort();
}

// Development helpers - expose to window/global for debugging
if (process.env.NODE_ENV !== 'production') {
  if (typeof window !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    (window as any).__getRegisteredComponents = getRegisteredComponentNames;
  }
  if (typeof global !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
    (global as any).__getRegisteredComponents = getRegisteredComponentNames;
  }
}
